/* eslint-disable no-empty */
import { useCallback } from 'react';
import { useKycNavigation } from '../useKycNavigation';
import { UseKycFormSubmissionProps } from './props';
import { getComposedKycSubmissionInput } from './helper';
import { useUserOriginId } from '../../user-identity/useUserOriginId';
import { useCreateKyc, useUpdateKycIdentity } from '../../../api';
import { KycCheckpointId, KYCStatus } from '../../../enums';
import { usePhoneNumberForm } from '../../user-phone-number';
import {
  CreateKycInputDto,
  CreateKycRequestDto,
  CreatePhoneNumberInputDto,
  UserEmailVerifyInputDto,
  UserPhoneNumberVerifyInputDto,
} from '../../../dto';
import { prefetchKycMeByOriginId } from '@starsoft/common/api/kyc/queries/useKycMeByOriginId/prefetch';
import { useAxiosInstance } from '../../useAxiosInstance';
import { useQueryClient } from '@tanstack/react-query';
import { useUserEmailsForm } from '../../user-emails/useUserEmailsForm';
import { Nullable } from '@starsoft/common/interfaces';

/**
 * Custom hook to manage KYC form submission.
 * This hook provides functionality to handle the submission of KYC forms, including creating and updating KYC records.
 *
 * @template T - The type of the form data.
 * @param {UseKycFormSubmissionProps<T>} props - The properties required for KYC form submission.
 * @returns {Object} - An object containing the handleKycSubmission function and isLoadingKycSubmission state.
 *
 * Example usage:
 * const { handleKycSubmission, isLoadingKycSubmission } = useKycFormSubmission<MyFormType>({
 *   callback: myCallbackFunction,
 *   kyc: myKycData,
 *   methods: myFormMethods,
 *   externalOriginId: '123',
 *   enabled: true,
 * });
 */
export function useKycFormSubmission<T>({
  callback,
  kyc,
  methods,
  externalOriginId,
  enabled = true,
}: UseKycFormSubmissionProps<T>) {
  // Destructure watch and setValue from methods if available
  const { watch, setValue } = methods ?? {};

  // Hook to create KYC and track its pending state
  const { createKycAsync, isPending: pendingCreateKycAsync } = useCreateKyc();

  // Hook to update KYC identity and track its pending state
  const { isPending: pendingUpdateKycAsync, updateKycIdentityAsync } =
    useUpdateKycIdentity();

  // Get the user's origin ID, falling back to externalOriginId if not available
  const originId: Nullable<string> =
    useUserOriginId() ?? externalOriginId?.toString();

  // Axios instance for making HTTP requests
  const instance = useAxiosInstance();

  // Query client for managing query cache
  const queryClient = useQueryClient();

  // Hook to manage phone number form submission
  const {
    onSubmit: createOrVerifyPhoneNumber,
    isPendingCreatePhoneNumber,
    isPendingVerifyUserPhoneNumber,
  } = usePhoneNumberForm<T>({
    watch,
    setValue,
  });

  // Hook to manage user email form submission
  const {
    userEmailId,
    submission: { verifyUserEmailAsync },
  } = useUserEmailsForm({ methods, useMinimalForm: true, enabled });

  // Hook to manage KYC navigation steps
  const { handleSetKycStep } = useKycNavigation({ callback });

  // Determine if any KYC submission process is loading
  const isLoadingKycSubmission =
    pendingCreateKycAsync ||
    pendingUpdateKycAsync ||
    isPendingCreatePhoneNumber ||
    isPendingVerifyUserPhoneNumber;

  /**
   * Function to handle the creation of a KYC record.
   *
   * @param {Partial<CreateKycInputDto>} input - The input data for creating a KYC record.
   * @returns {Promise<any>} - The created KYC record.
   *
   * Example:
   * const createdKyc = await handleCreateKyc({ originId: '123', identityId: '456' });
   */
  const handleCreateKyc = useCallback(
    async (input: Partial<CreateKycInputDto>) => {
      const createdKyc = await createKycAsync(input as CreateKycInputDto);

      if (createdKyc?.status == KYCStatus.Verified) {
        return createdKyc;
      }

      if (createdKyc) {
        handleSetKycStep(createdKyc?.stage?.checkpoint?.id);
      }

      return createdKyc;
    },
    [createKycAsync, handleSetKycStep],
  );

  /**
   * Function to handle the update of a KYC record.
   *
   * @param {CreateKycRequestDto} data - The data for updating a KYC record.
   * @returns {Promise<any>} - The updated KYC record.
   *
   * Example:
   * const updatedKyc = await handleUpdateKyc({ identityId: '456' });
   */
  const handleUpdateKyc = useCallback(
    async (data: CreateKycRequestDto) => {
      const updatedKyc = await updateKycIdentityAsync({
        id: kyc?.id?.toString(),
        identityId: data?.identityId,
      });

      if (updatedKyc?.status == KYCStatus.Verified) {
        return updatedKyc;
      }
      handleSetKycStep(updatedKyc?.stage?.checkpoint?.id);

      return updatedKyc;
    },
    [handleSetKycStep, kyc?.id, updateKycIdentityAsync],
  );

  /**
   * Function to fetch the updated KYC record.
   *
   * @returns {Promise<any>} - The updated KYC record.
   *
   * Example:
   * const updatedKyc = await handleGetUpdatedKyc();
   */
  const handleGetUpdatedKyc = useCallback(async () => {
    const updatedKyc = await prefetchKycMeByOriginId({
      queryClient,
      originId,
      instance,
    });
    return updatedKyc;
  }, [queryClient, originId, instance]);

  /**
   * Function to handle the submission of KYC data.
   *
   * @param {CreateKycRequestDto} data - The KYC data to be submitted.
   * @returns {Promise<any>} - The result of the KYC submission.
   *
   * Example:
   * const result = await handleKycSubmission({ identityId: '456', code: '1234' });
   */
  const handleKycSubmission = useCallback(
    async (data: CreateKycRequestDto) => {
      try {
        const input: Partial<CreateKycInputDto> = getComposedKycSubmissionInput(
          originId,
          data,
        );

        if (!kyc) {
          return handleCreateKyc(input);
        }

        switch (kyc?.status) {
          case KYCStatus.Created:
            switch (kyc?.stage?.checkpoint?.id?.toString()) {
              case KycCheckpointId.VerifiedTaxId?.toString():
                await updateKycIdentityAsync({
                  id: kyc?.id?.toString(),
                  identityId: data?.identityId,
                });

                return await handleGetUpdatedKyc();
              case KycCheckpointId.Identity?.toString():
                return handleUpdateKyc(data);
              case KycCheckpointId.VerifiedPhoneNumber?.toString(): {
                createOrVerifyPhoneNumber(
                  data as CreatePhoneNumberInputDto &
                    UserPhoneNumberVerifyInputDto,
                );
                return await handleGetUpdatedKyc();
              }
              case KycCheckpointId.VerifiedEmail?.toString(): {
                await verifyUserEmailAsync({
                  emailId: userEmailId?.toString(),
                  code: (data as UserEmailVerifyInputDto)?.code,
                });

                return await handleGetUpdatedKyc();
              }
              case KycCheckpointId.DocumentSubmission?.toString(): {
                return await handleGetUpdatedKyc();
              }

              case KycCheckpointId.AddressVerification?.toString():
              case KycCheckpointId.ProofOfIncome?.toString():
              case KycCheckpointId.ProofOfResidence?.toString():
              case KycCheckpointId.CriminalRecordCheck?.toString():
            }
            break;
          case KYCStatus.Verified:
            return kyc;
        }
      } catch (err: unknown) {
        console.error('Error in handleKycSubmission:', err);
      }
    },
    [
      kyc,
      originId,
      handleCreateKyc,
      handleUpdateKyc,
      createOrVerifyPhoneNumber,
      updateKycIdentityAsync,
      handleGetUpdatedKyc,
      verifyUserEmailAsync,
      userEmailId,
    ],
  );

  // Return the handleKycSubmission function and the loading state
  return { handleKycSubmission, isLoadingKycSubmission };
}

export type { UseKycFormSubmissionProps };
